import {
  configureStore,
  ThunkAction,
  Action,
  combineReducers,
  PreloadedState
} from "@reduxjs/toolkit";
import usersReducer from "../features/Users/usersSlice";
import programsReducer from "../features/Programs/programsSlice";
import institutionsReducer from "../features/Institutions/institutionsSlice";
import toastReducer from "../common-ui/toastSlice";
import modalReducer from "../common-ui/modalSlice";
import networksReducer from "../features/Networks/networksSlice";
import networkProgramsReducer from "../features/Networks/networkProgramsSlice";
import firebaseReducer from "../features/Firebase/firebaseSlice";
import messageQueueReducer from "../features/MessageQueue/messageQueueSlice";

export const store = configureStore({
  reducer: {
    users: usersReducer,
    programs: programsReducer,
    institutions: institutionsReducer,
    toast: toastReducer,
    modal: modalReducer,
    networks: networksReducer,
    networkPrograms: networkProgramsReducer,
    firebase: firebaseReducer,
    messageQueue: messageQueueReducer
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        // Ignore these paths in the state that contain non-serializable values (React elements)
        ignoredActions: [
          "modal/setModalContent",
          "messageQueue/setError",
          "messageQueue/setMessages",
          "messageQueue/setStatus",
          "modal/setModalBoxStyle"
        ],
        ignoredActionsPaths: ["payload.content", "payload.$$typeof"],
        ignoredPaths: ["modal.content", "modal.boxStyle", "messageQueue.modal.content"]
      }
    })
});

// Separate the root reducer so that we have access to the RootState 'type'
const rootReducer = combineReducers({
  users: usersReducer,
  programs: programsReducer,
  institutions: institutionsReducer,
  toast: toastReducer,
  modal: modalReducer,
  networks: networksReducer,
  networkPrograms: networkProgramsReducer,
  firebase: firebaseReducer,
  messageQueue: messageQueueReducer
});

// Creating an extra method to configure the store programmatically
export const setupStore =
  // preloadedState allows us to use a mock store for testing
  (preloadedState?: PreloadedState<RootState>) => {
    return configureStore({
      reducer: rootReducer,
      preloadedState: preloadedState,
      middleware: (getDefaultMiddleware) =>
        getDefaultMiddleware({
          serializableCheck: {
            // Ignore these paths in the state that contain non-serializable values (React elements)
            ignoredActions: [
              "modal/setModalContent",
              "messageQueue/setError",
              "messageQueue/setMessages",
              "messageQueue/setStatus",
              "modal/setModalBoxStyle"
            ],
            ignoredActionsPaths: ["payload.content", "payload.$$typeof"],
            ignoredPaths: ["modal.content", "messageQueue.modal.content", "modal.boxStyle"]
          }
        })
    });
  };

export type RootState = ReturnType<typeof rootReducer>;
export type AppStore = ReturnType<typeof setupStore>;
export type AppDispatch = AppStore["dispatch"];
export type AppThunk<ReturnType = void> = ThunkAction<
  ReturnType,
  RootState,
  unknown,
  Action<string>
>;
